import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../App';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FaLock, FaCrown, FaArrowUp } from 'react-icons/fa';

/**
 * SubscriptionGate - Blocks content if user has reached their subscription limit
 * 
 * @param {string} adType - Type of ad: 'product', 'rental', 'request', 'directory'
 * @param {string} gateType - Type of gate: 'ad' (for ads) or 'chantier' (for project lists)
 * @param {ReactNode} children - Content to show if allowed
 * @param {function} onAllowed - Callback when user is allowed (optional)
 * @param {function} onBlocked - Callback when user is blocked (optional)
 */
export default function SubscriptionGate({ 
  adType, 
  gateType = 'ad',
  children, 
  onAllowed,
  onBlocked,
  showBlockedUI = true 
}) {
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [canProceed, setCanProceed] = useState(false);
  const [blockMessage, setBlockMessage] = useState('');
  const [reason, setReason] = useState('');

  const checkAccess = useCallback(async () => {
    if (!user) {
      setCanProceed(false);
      setBlockMessage('Veuillez vous connecter pour continuer');
      setReason('not_logged_in');
      setChecking(false);
      if (onBlocked) onBlocked('not_logged_in');
      return;
    }

    try {
      let response;
      
      if (gateType === 'chantier') {
        response = await axios.get(`${API}/subscriptions/user/${user.id}/can-create-chantier`);
      } else {
        response = await axios.get(`${API}/subscriptions/user/${user.id}/can-create?ad_type=${adType}`);
      }

      const data = response.data;
      
      if (data.can_create) {
        setCanProceed(true);
        if (onAllowed) onAllowed(data);
      } else {
        setCanProceed(false);
        setBlockMessage(data.message_fr || 'Limite atteinte');
        setReason(data.reason || 'limit_reached');
        if (onBlocked) onBlocked(data.reason);
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
      setCanProceed(false);
      setBlockMessage('Erreur lors de la vérification de votre abonnement');
      setReason('error');
    } finally {
      setChecking(false);
    }
  }, [API, user, adType, gateType, onAllowed, onBlocked]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  if (checking) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2540]"></div>
      </div>
    );
  }

  if (canProceed) {
    return <>{children}</>;
  }

  if (!showBlockedUI) {
    return null;
  }

  // Blocked UI
  return (
    <Card className="border-2 border-yellow-400 bg-gradient-to-br from-yellow-50 to-orange-50">
      <CardContent className="p-8 text-center">
        <div className="bg-yellow-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
          <FaLock className="text-4xl text-yellow-600" />
        </div>
        
        <h3 className="text-xl font-bold text-gray-800 mb-3">
          {reason === 'subscription_expired' && 'Abonnement Expiré'}
          {reason === 'limit_reached' && 'Limite Atteinte'}
          {reason === 'plan_not_allowed' && 'Fonctionnalité Non Disponible'}
          {reason === 'not_allowed' && 'Action Non Autorisée'}
          {reason === 'not_logged_in' && 'Connexion Requise'}
          {reason === 'error' && 'Erreur'}
        </h3>
        
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          {blockMessage}
        </p>

        {reason === 'not_logged_in' ? (
          <Button 
            className="bg-[#0A2540] hover:bg-[#003d56]"
            onClick={() => navigate('/login')}
          >
            Se connecter
          </Button>
        ) : (
          <div className="space-y-3">
            <Button 
              className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
              onClick={() => navigate('/pricing')}
            >
              <FaArrowUp className="mr-2" />
              Voir les offres
            </Button>
            <p className="text-sm text-gray-500">
              Passez à un plan supérieur pour débloquer plus de fonctionnalités
            </p>
          </div>
        )}

        {/* Plan comparison hint */}
        {reason === 'limit_reached' && (
          <div className="mt-6 pt-6 border-t text-left max-w-sm mx-auto">
            <p className="text-sm font-medium text-gray-700 mb-2">
              Comparaison des plans :
            </p>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>• <strong>Découverte (99€/mois)</strong> : 1 annonce au total</li>
              <li>• <strong>Pro (1500€/an)</strong> : 1 annonce par catégorie + chantiers</li>
              <li>• <strong>Fournisseur (2000€/an)</strong> : 20 annonces + boutique</li>
            </ul>
          </div>
        )}

        {reason === 'plan_not_allowed' && gateType === 'chantier' && (
          <div className="mt-6 pt-6 border-t text-left max-w-sm mx-auto">
            <p className="text-sm font-medium text-gray-700 mb-2">
              <FaCrown className="inline mr-1 text-yellow-500" />
              Les listes de chantier sont disponibles avec :
            </p>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>• Pack <strong>Pro</strong> (1500€/an)</li>
              <li>• Pack <strong>Sur Mesure</strong></li>
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Hook to check subscription limits before an action
 */
export function useSubscriptionCheck() {
  const { API, user } = useContext(AppContext);

  const checkAdLimit = async (adType) => {
    if (!user) return { allowed: false, reason: 'not_logged_in' };
    
    try {
      const response = await axios.get(`${API}/subscriptions/user/${user.id}/can-create?ad_type=${adType}`);
      return {
        allowed: response.data.can_create,
        reason: response.data.reason,
        message: response.data.message_fr
      };
    } catch (error) {
      return { allowed: false, reason: 'error', message: 'Erreur de vérification' };
    }
  };

  const checkChantierLimit = async () => {
    if (!user) return { allowed: false, reason: 'not_logged_in' };
    
    try {
      const response = await axios.get(`${API}/subscriptions/user/${user.id}/can-create-chantier`);
      return {
        allowed: response.data.can_create,
        reason: response.data.reason,
        message: response.data.message_fr
      };
    } catch (error) {
      return { allowed: false, reason: 'error', message: 'Erreur de vérification' };
    }
  };

  const incrementAdCount = async (adType) => {
    if (!user) return;
    try {
      await axios.post(`${API}/subscriptions/user/${user.id}/increment-ad?ad_type=${adType}`);
    } catch (error) {
      console.error('Error incrementing ad count:', error);
    }
  };

  return { checkAdLimit, checkChantierLimit, incrementAdCount };
}
